import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Plus, Users, CheckCircle2, Clock, TrendingUp, Link2, Settings2,
  Trash2, CalendarDays, MapPin, ArrowRight, PartyPopper, ClipboardList,
} from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { eventsApi } from "@/lib/api";
import { formatShortDate } from "@/lib/helpers";
import { useCan } from "@/context/AuthContext";
import { eventUrl } from "@/lib/eventUrl";

function StatCard({ icon: Icon, label, value, testId, accent }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-5" data-testid={testId}>
      <div className="flex items-center justify-between">
        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${accent}`}>
          <Icon className="h-5 w-5" />
        </span>
      </div>
      <p className="mt-4 font-serif text-3xl font-semibold">{value}</p>
      <p className="mt-1 text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

export default function Dashboard() {
  const canDelete = useCan("delete");
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [statsMap, setStatsMap] = useState({});
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const evs = await eventsApi.list();
      setEvents(evs);
      const stats = {};
      await Promise.all(
        evs.map(async (e) => {
          stats[e.id] = await eventsApi.stats(e.id);
        })
      );
      setStatsMap(stats);
    } catch {
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const totals = Object.values(statsMap).reduce(
    (a, s) => ({
      invited: a.invited + s.total_invited,
      attending: a.attending + s.attending,
      pending: a.pending + s.pending,
      heads: a.heads + s.total_heads,
    }),
    { invited: 0, attending: 0, pending: 0, heads: 0 }
  );

  const copyLink = (ev) => {
    navigator.clipboard.writeText(eventUrl(ev));
    toast.success("RSVP link copied to clipboard");
  };

  const del = async (id) => {
    if (!window.confirm("Delete this event and all its responses?")) return;
    await eventsApi.remove(id);
    toast.success("Event deleted");
    load();
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="eyebrow text-gold">Host dashboard</p>
            <h1 className="mt-2 font-serif text-3xl font-semibold tracking-tight sm:text-4xl">Your events</h1>
          </div>
          <button
            data-testid="dashboard-create-btn"
            onClick={() => navigate("/create")}
            className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground transition-all hover:shadow-lg"
          >
            <Plus className="h-4 w-4" /> New Event
          </button>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={Users} label="Total invited" value={totals.invited} testId="stat-total-invited" accent="bg-secondary text-foreground" />
          <StatCard icon={CheckCircle2} label="Attending" value={totals.attending} testId="stat-total-attending" accent="bg-emerald-50 text-emerald-700" />
          <StatCard icon={Clock} label="Awaiting reply" value={totals.pending} testId="stat-total-pending" accent="bg-amber-50 text-amber-700" />
          <StatCard icon={TrendingUp} label="Guests coming" value={totals.heads} testId="stat-total-heads" accent="bg-gold/15 text-gold" />
        </div>

        <div className="mt-10">
          {loading ? (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {[0, 1, 2].map((i) => (
                <div key={i} className="h-72 animate-pulse rounded-2xl bg-secondary/60" />
              ))}
            </div>
          ) : events.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border bg-card p-16 text-center">
              <PartyPopper className="mx-auto h-10 w-10 text-gold" />
              <h3 className="mt-4 font-serif text-2xl font-medium">No events yet</h3>
              <p className="mt-2 text-sm text-muted-foreground">Create your first event and start collecting RSVPs.</p>
              <button onClick={() => navigate("/create")} className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground">
                <Plus className="h-4 w-4" /> Create Event
              </button>
            </div>
          ) : (
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid="events-grid">
              {events.map((ev) => {
                const s = statsMap[ev.id] || {};
                return (
                  <div key={ev.id} data-testid={`event-card-${ev.id}`} className="group overflow-hidden rounded-2xl border border-border bg-card transition-all hover:shadow-xl">
                    <div className="relative h-40 bg-cover bg-center" style={{ backgroundImage: `url(${ev.cover_image})` }}>
                      <div className="absolute inset-0 bg-gradient-to-t from-black/75 to-transparent" />
                      <span className="absolute left-4 top-4 rounded-full bg-black/40 px-3 py-1 text-xs font-medium text-white backdrop-blur">
                        {ev.event_type}
                      </span>
                      <div className="absolute bottom-3 left-4 right-4 text-white">
                        <p className="font-serif text-xl font-semibold leading-tight">{ev.title}</p>
                      </div>
                    </div>
                    <div className="p-5">
                      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
                        <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4 text-gold" />{formatShortDate(ev.date)}</span>
                        <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4 text-gold" />{ev.venue_name || "TBA"}</span>
                      </div>

                      <div className="mt-4 grid grid-cols-3 gap-2 rounded-xl bg-secondary/50 p-3 text-center">
                        <div>
                          <p className="font-serif text-lg font-semibold text-emerald-700">{s.attending ?? 0}</p>
                          <p className="text-xs text-muted-foreground">Yes</p>
                        </div>
                        <div>
                          <p className="font-serif text-lg font-semibold text-destructive">{s.declined ?? 0}</p>
                          <p className="text-xs text-muted-foreground">No</p>
                        </div>
                        <div>
                          <p className="font-serif text-lg font-semibold text-amber-600">{s.pending ?? 0}</p>
                          <p className="text-xs text-muted-foreground">Pending</p>
                        </div>
                      </div>

                      <div className="mt-4 flex items-center gap-2">
                        <Link
                          to={`/events/${ev.id}/manage`}
                          data-testid={`manage-event-${ev.id}`}
                          className="flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-primary px-3 py-2.5 text-sm font-semibold text-primary-foreground transition-opacity hover:opacity-90"
                        >
                          Manage <ArrowRight className="h-4 w-4" />
                        </Link>
                        <button onClick={() => copyLink(ev)} data-testid={`copy-link-${ev.id}`} title="Copy RSVP link" className="rounded-xl border border-border p-2.5 transition-colors hover:bg-secondary">
                          <Link2 className="h-4 w-4" />
                        </button>
                        <button onClick={() => navigate(`/events/${ev.id}/forms`)} data-testid={`forms-event-${ev.id}`} title="Guest Forms" className="rounded-xl border border-border p-2.5 transition-colors hover:bg-secondary">
                          <ClipboardList className="h-4 w-4" />
                        </button>
                        <button onClick={() => navigate(`/edit/${ev.id}`)} data-testid={`edit-event-${ev.id}`} title="Edit" className="rounded-xl border border-border p-2.5 transition-colors hover:bg-secondary">
                          <Settings2 className="h-4 w-4" />
                        </button>
                        {canDelete && (<button onClick={() => del(ev.id)} data-testid={`delete-event-${ev.id}`} title="Delete" className="rounded-xl border border-border p-2.5 text-destructive transition-colors hover:bg-destructive/10">
                          <Trash2 className="h-4 w-4" />
                        </button>)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
